import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useRememberedSection } from './navigation-preferences';
import { useEffect, useRef, useState } from 'react';
import type { SessionViewerResult } from '../auth/api';
import authForge from '../assets/auth/tosho-login-forge.webp';
import toshoLogo from '../assets/tosho-logo.svg';
import { TOSHO_PRODUCT_CHANGELOG } from '../product-changelog';
import { TOSHO_PRODUCT_VERSION } from '../product-version';
import '../styles/modules-supervisor-administration.css';
import { MyAccountDialog } from './MyAccountDialog';
import { PrototypeIcon } from './PrototypeIcon';
import { SupervisorAdministration, type SupervisorSection } from './SupervisorAdministration';
import { SupervisorDashboard } from './SupervisorDashboard';
import { SupervisorOrganizations } from './SupervisorOrganizations';
import { SupervisorUsers } from './SupervisorUsers';

interface SupervisorShellProps {
  readonly viewer: SessionViewerResult;
  readonly csrfToken: string;
  readonly onReauthenticate: () => void;
  readonly onLogout: () => void;
  readonly logoutPending?: boolean;
  readonly notificationCount?: number;
}

type SupervisorShellSection = SupervisorSection | 'dashboard' | 'organizations' | 'users';

const COMPACT_SIDEBAR_MEDIA = '(min-width: 901px) and (max-width: 1434px)';

function shouldStartWithCompactSidebar(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(COMPACT_SIDEBAR_MEDIA).matches;
}

function useCloseOnEscape(onClose: () => void): void {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
}

export function SupervisorShell({
  viewer,
  csrfToken,
  onLogout,
  logoutPending = false,
  notificationCount = 0,
}: SupervisorShellProps): React.JSX.Element {
  const [section, setSection] = useRememberedSection<SupervisorShellSection>(
    'section',
    'dashboard',
    ['dashboard', 'organizations', 'administrators', 'users', 'settings'],
  );
  const [whyToshoOpen, setWhyToshoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(shouldStartWithCompactSidebar);
  const [sidebarManuallySet, setSidebarManuallySet] = useState(false);

  useEffect(() => {
    if (sidebarManuallySet) return undefined;

    const compactMedia = window.matchMedia(COMPACT_SIDEBAR_MEDIA);
    const syncAutomaticState = (): void => setSidebarCollapsed(compactMedia.matches);
    syncAutomaticState();
    compactMedia.addEventListener('change', syncAutomaticState);
    return () => compactMedia.removeEventListener('change', syncAutomaticState);
  }, [sidebarManuallySet]);

  const safeNotificationCount = Math.max(0, Math.floor(notificationCount));

  return (
    <>
      <main className={`l-app-shell${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
        <aside className="app-sidebar" aria-label="Navigation principale">
          <nav className="app-navigation supervisor-navigation">
            <div className="app-navigation__group">
              <button
                className={`app-navigation__item${section === 'dashboard' ? ' is-active' : ''}`}
                type="button"
                aria-current={section === 'dashboard' ? 'page' : undefined}
                data-tooltip="Tableau de bord"
                onClick={() => setSection('dashboard')}
              >
                <PrototypeIcon className="app-navigation__icon" name="layout-grid" />
                <span>Tableau de bord</span>
              </button>
              <button
                className={`app-navigation__item${section === 'organizations' ? ' is-active' : ''}`}
                type="button"
                aria-current={section === 'organizations' ? 'page' : undefined}
                data-tooltip="Organisations"
                onClick={() => setSection('organizations')}
              >
                <PrototypeIcon className="app-navigation__icon" name="briefcase-business" />
                <span>Organisations</span>
              </button>
              <button
                className={`app-navigation__item${section === 'administrators' ? ' is-active' : ''}`}
                type="button"
                aria-current={section === 'administrators' ? 'page' : undefined}
                data-tooltip="Administrateurs"
                onClick={() => setSection('administrators')}
              >
                <PrototypeIcon className="app-navigation__icon" name="users-round" />
                <span>Administrateurs</span>
              </button>
              <button
                className={`app-navigation__item${section === 'users' ? ' is-active' : ''}`}
                type="button"
                aria-current={section === 'users' ? 'page' : undefined}
                data-tooltip="Utilisateurs"
                onClick={() => setSection('users')}
              >
                <PrototypeIcon className="app-navigation__icon" name="network" />
                <span>Utilisateurs</span>
              </button>
              <button
                className={`app-navigation__item${section === 'settings' ? ' is-active' : ''}`}
                type="button"
                aria-current={section === 'settings' ? 'page' : undefined}
                data-tooltip="Configuration"
                onClick={() => setSection('settings')}
              >
                <PrototypeIcon className="app-navigation__icon" name="settings" />
                <span>Configuration</span>
              </button>
            </div>
          </nav>

          <div className="app-sidebar__lower">
            <div className="app-sidebar__tosho-footer" aria-label="Tosho">
              <span className="app-sidebar__logo-row">
                <img className="app-sidebar__tosho-logo" src={toshoLogo} alt="Tosho" />
                <button
                  className="app-sidebar__why-tosho"
                  type="button"
                  onClick={() => setWhyToshoOpen(true)}
                  aria-label="Pourquoi Tosho ?"
                  aria-haspopup="dialog"
                  title="Pourquoi Tosho ?"
                >
                  <PrototypeIcon name="circle-help" />
                </button>
              </span>
              <span className="app-sidebar__version-row">
                <small>{TOSHO_PRODUCT_VERSION}</small>
                <button
                  className="app-sidebar__changelog"
                  type="button"
                  onClick={() => setChangelogOpen(true)}
                  aria-label="Voir le changelog Tosho"
                  aria-haspopup="dialog"
                  title="Changelog"
                >
                  <PrototypeIcon name="history" />
                </button>
              </span>
            </div>

            <div className="app-sidebar__footer">
              <PrototypeIcon className="app-sidebar__security-icon" name="shield-check" />
              <span>
                <strong>Accès sécurisé</strong>
                <small>
                  Tosho protège vos données confidentielles avec les dernières technologies de
                  chiffrement et d’identification.
                </small>
              </span>
            </div>
          </div>
        </aside>

        <button
          className="app-sidebar__toggle"
          type="button"
          onClick={() => {
            setSidebarManuallySet(true);
            setSidebarCollapsed((current) => !current);
          }}
          aria-label={sidebarCollapsed ? 'Déplier le menu latéral' : 'Replier le menu latéral'}
          aria-pressed={sidebarCollapsed}
          title={sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'}
        >
          <PrototypeIcon className="app-sidebar__toggle-icon" name="chevron-down" />
        </button>

        <section className="l-app-main">
          <header className="app-topbar">
            <div className="app-search app-search--inline" role="search">
              <PrototypeIcon className="app-search__icon" name="search" />
              <input
                className="app-search__input"
                type="search"
                role="combobox"
                aria-label="Rechercher partout dans Tosho"
                aria-autocomplete="list"
                aria-expanded={false}
                aria-controls="supervisor-search-inline-suggestions"
                placeholder="Rechercher partout dans Tosho..."
              />
              <kbd>⌘ K</kbd>
              <ul id="supervisor-search-inline-suggestions" role="listbox" hidden />
            </div>

            <button
              className="app-search-trigger"
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Ouvrir la recherche globale"
              aria-haspopup="dialog"
            >
              <PrototypeIcon name="search" />
            </button>

            <div className="app-topbar__actions">
              <button
                className="app-icon-button app-notifications"
                type="button"
                disabled
                aria-label="Notifications"
              >
                <PrototypeIcon name="bell" />
                {safeNotificationCount > 0 ? (
                  <span className="app-notifications__badge">
                    {safeNotificationCount > 99 ? '99+' : safeNotificationCount}
                  </span>
                ) : null}
              </button>

              <details className="app-profile">
                <summary className="app-profile__summary">
                  <span className="app-profile__avatar" aria-hidden="true">
                    {viewer.givenName.slice(0, 1).toUpperCase() || 'T'}
                  </span>
                  <span className="app-profile__identity">
                    <strong>
                      {viewer.givenName} {viewer.familyName}
                    </strong>
                    <small>Superviseur de plateforme</small>
                  </span>
                  <PrototypeIcon className="app-profile__chevron" name="chevron-down" />
                </summary>
                <div className="app-profile__menu">
                  <button
                    className="app-profile__account"
                    type="button"
                    onClick={() => setAccountOpen(true)}
                  >
                    Mon compte
                  </button>
                  <button
                    className="app-profile__logout"
                    type="button"
                    onClick={onLogout}
                    disabled={logoutPending}
                  >
                    {logoutPending ? 'Déconnexion…' : 'Déconnexion'}
                  </button>
                </div>
              </details>
            </div>
          </header>

          {section === 'dashboard' ? <SupervisorDashboard /> : null}
          {section === 'organizations' ? <SupervisorOrganizations /> : null}
          {section === 'users' ? <SupervisorUsers /> : null}
          {section === 'administrators' || section === 'settings' ? (
            <SupervisorAdministration
              section={section}
              csrfToken={csrfToken}
              onSessionsPurged={() => window.location.replace('/')}
            />
          ) : null}
        </section>
      </main>

      {searchOpen ? <SupervisorGlobalSearchDialog onClose={() => setSearchOpen(false)} /> : null}
      {changelogOpen ? <SupervisorChangelogDialog onClose={() => setChangelogOpen(false)} /> : null}
      {whyToshoOpen ? <SupervisorWhyToshoDialog onClose={() => setWhyToshoOpen(false)} /> : null}
      {accountOpen ? (
        <MyAccountDialog viewer={viewer} onClose={() => setAccountOpen(false)} />
      ) : null}
    </>
  );
}

function SupervisorGlobalSearchDialog({
  onClose,
}: {
  readonly onClose: () => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  useCloseOnEscape(onClose);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop global-search-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-label="Recherche globale"
      close={
        <ModalCloseButton
          className="tosho-modal-close global-search-dialog__close"
          onClick={onClose}
          aria-label="Fermer la recherche"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel global-search-dialog">
        <div className="global-search-dialog__field" role="search">
          <PrototypeIcon className="global-search-dialog__icon" name="search" />
          <input
            ref={inputRef}
            className="global-search-dialog__input"
            type="search"
            role="combobox"
            aria-label="Rechercher partout dans Tosho"
            aria-autocomplete="list"
            aria-expanded={false}
            aria-controls="supervisor-search-dialog-suggestions"
            placeholder="Rechercher partout dans Tosho..."
          />
        </div>
        <ul
          id="supervisor-search-dialog-suggestions"
          className="global-search-dialog__suggestions"
          role="listbox"
          hidden
        />
      </section>
    </ModalBackdrop>
  );
}

function SupervisorChangelogDialog({
  onClose,
}: {
  readonly onClose: () => void;
}): React.JSX.Element {
  useCloseOnEscape(onClose);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop changelog-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="supervisor-changelog-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close changelog-dialog__close"
          onClick={onClose}
          aria-label="Fermer le changelog"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel changelog-dialog">
        <header className="changelog-dialog__header tosho-modal-header">
          <div>
            <p>Évolutions publiées</p>
            <h2 id="supervisor-changelog-title">Changelog Tosho</h2>
          </div>
        </header>
        <div className="changelog-dialog__body">
          <p className="changelog-dialog__intro">
            Les principales améliorations visibles de Tosho, expliquées simplement.
          </p>
          <ol className="changelog-list">
            {TOSHO_PRODUCT_CHANGELOG.map((entry) => (
              <li className="changelog-entry" key={`${entry.version}-${entry.date}`}>
                <div className="changelog-entry__meta">
                  <strong>{entry.version}</strong>
                  <time>{entry.date}</time>
                </div>
                <h3>{entry.title}</h3>
                <ul>
                  {entry.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </ModalBackdrop>
  );
}

function SupervisorWhyToshoDialog({
  onClose,
}: {
  readonly onClose: () => void;
}): React.JSX.Element {
  useCloseOnEscape(onClose);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop why-tosho-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="supervisor-why-tosho-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close why-tosho-dialog__close"
          onClick={onClose}
          aria-label="Fermer"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel why-tosho-dialog">
        <header className="why-tosho-dialog__header tosho-modal-header">
          <img className="why-tosho-dialog__forge" src={authForge} alt="" aria-hidden="true" />
          <div className="why-tosho-dialog__veil" aria-hidden="true" />

          <div className="why-tosho-dialog__heading">
            <span className="why-tosho-dialog__kanji" lang="ja">
              刀匠
            </span>
            <h2 id="supervisor-why-tosho-title">Pourquoi Tosho ?</h2>
            <p>La forge de KATANA</p>
          </div>
        </header>
        <div className="why-tosho-dialog__body">
          <p>
            <strong>Tōshō</strong> désigne le maître forgeron qui façonnait les lames des samouraïs
            dans le Japon féodal.
          </p>
          <p>
            Chez KATANA, chaque projet client est une lame forgée sur mesure : un outil précis que
            nous plaçons entre ses mains pour affronter le monde des affaires.
          </p>
          <p>
            Tosho réunit le pilotage de notre activité, les projets, le SAV, les FAQ et notre
            mémoire technique. La plateforme apprend de nos réussites comme de nos erreurs pour
            rendre chaque réalisation plus solide que la précédente.
          </p>
          <button className="why-tosho-dialog__return" type="button" onClick={onClose}>
            Revenir à la forge
          </button>
        </div>
      </section>
    </ModalBackdrop>
  );
}
